import { useRef, useState } from "react";

import { Button, Dialog, DialogClose, Field, Input, Money, Notice, Textarea } from "../../../../components/ui";
import { actionError, newHandoverKey, periodName } from "./payrollFinance";

function Facts({ rows }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 rounded-panel bg-surface-sunken px-4 py-3 text-body-sm">
      {rows.map(([term, value]) => (
        <div key={term} className="contents">
          <dt className="text-secondary">{term}</dt>
          <dd className="min-w-0 text-foreground">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Record cash handover — "I handed ₱X in cash to this employee." It never
 * marks the salary paid: only the employee's confirmation in My HR does
 * (FIN-2). The exact net amount is sent; a request key per salary makes a
 * retry after a lost response safe.
 */
export function HandoverDialog({ salary, period, onClose, onRecorded, recordHandover }) {
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [failure, setFailure] = useState("");
  const [busy, setBusy] = useState(false);
  const keyRef = useRef(null);
  if (!keyRef.current) keyRef.current = newHandoverKey(salary.recordId);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setFailure("");
    try {
      await recordHandover(
        salary.recordId,
        { method: "cash", amountCents: salary.netPayCents, reference: reference.trim() || undefined, notes: notes.trim() || undefined },
        keyRef.current
      );
      await onRecorded(salary);
    } catch (error) {
      setFailure(actionError(error, "The handover wasn't recorded. Try again — it won't be recorded twice."));
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => { if (!open && !busy) onClose(); }}
      title="Record cash handover"
      description={`Record this after you give the cash. The salary is paid once ${salary.employeeName} confirms receiving it in My HR.`}
      footer={
        <>
          <DialogClose asChild>
            <Button disabled={busy}>Cancel</Button>
          </DialogClose>
          <Button variant="primary" type="submit" form="handover-form" loading={busy}>
            <span>I handed over <Money value={salary.netPayCents} /></span>
          </Button>
        </>
      }
    >
      <form id="handover-form" onSubmit={submit} noValidate className="space-y-4">
        <Facts rows={[
          ["Employee", salary.employeeName],
          ["Payroll", periodName(period)],
          ["Cash", <Money key="amount" value={salary.netPayCents} className="text-money" />],
        ]} />
        {failure ? <Notice tone="danger" role="alert">{failure}</Notice> : null}
        <Field label="Reference" hint="Optional — an envelope or voucher number.">
          <Input value={reference} maxLength={100} onChange={(event) => setReference(event.target.value)} />
        </Field>
        <Field label="Notes" hint="Optional.">
          <Textarea value={notes} maxLength={500} onChange={(event) => setNotes(event.target.value)} />
        </Field>
      </form>
    </Dialog>
  );
}

const REASON_MIN = 10;

/**
 * Confirm on the employee's behalf — the owner-only exception for someone who
 * cannot confirm themselves. It is recorded as the owner's confirmation, with
 * a reason, never as the employee's.
 */
export function OnBehalfDialog({ salary, onClose, onConfirmed, confirmOnBehalf }) {
  const [reason, setReason] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [failure, setFailure] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    const value = reason.trim();
    if (value.length < REASON_MIN) {
      setFieldError(`Give a reason of at least ${REASON_MIN} characters.`);
      return;
    }
    setBusy(true);
    setFailure("");
    try {
      await confirmOnBehalf(salary.recordId, { reason: value });
      await onConfirmed(salary);
    } catch (error) {
      setFailure(actionError(error, "The confirmation wasn't saved. Try again."));
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => { if (!open && !busy) onClose(); }}
      title="Confirm on the employee's behalf?"
      description={`Only when ${salary.employeeName} can't confirm themselves — for example, they left and have no access. It is recorded as confirmed by you, not by the employee.`}
      footer={
        <>
          <DialogClose asChild>
            <Button disabled={busy}>Cancel</Button>
          </DialogClose>
          <Button variant="primary" type="submit" form="on-behalf-form" loading={busy}>
            <span>Confirm <Money value={salary.payment?.amountCents ?? salary.netPayCents} /> received</span>
          </Button>
        </>
      }
    >
      <form id="on-behalf-form" onSubmit={submit} noValidate className="space-y-4">
        {failure ? <Notice tone="danger" role="alert">{failure}</Notice> : null}
        <Field label="Why can't the employee confirm?" error={fieldError} required>
          <Textarea
            value={reason}
            maxLength={500}
            onChange={(event) => {
              setReason(event.target.value);
              if (fieldError) setFieldError("");
            }}
          />
        </Field>
      </form>
    </Dialog>
  );
}
